// src/components/payments/Subscriptions.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { paymentsService } from '../../services/paymentsService';
import { licenseService } from '../../services/licenseService';
import { organizationService } from '../../services/organizationService';
import LoadingSpinner from '../common/LoadingSpinner';
import PaymentMethodModal from './PaymentMethodModal';
import ManualPaymentModal from './ManualPaymentModal';
import DiscountRequestModal from './DiscountRequestModal';
import SubscriptionInvoice from './SubscriptionInvoice';
import PaymentReceipt from './PaymentReceipt';
import RefundRequest from './RefundRequest';
import Toast from '../common/Toast';
import { usePageTitle } from '../../hooks/usePageTitle';
import './Subscriptions.css';

const Subscriptions = () => {
    usePageTitle('Subscriptions');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [subscription, setSubscription] = useState(null);
    const [paymentPlans, setPaymentPlans] = useState([]);
    const [plansLoaded, setPlansLoaded] = useState(false);
    const [paymentGateways, setPaymentGateways] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [activeTab, setActiveTab] = useState('subscription');

    // Modal states for new components
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
    const [manualPaymentData, setManualPaymentData] = useState(null);

    // Search state for plans dropdown
    const [planSearchTerm, setPlanSearchTerm] = useState('');
    const [showPlanDropdown, setShowPlanDropdown] = useState(false);

    // Pagination state for payment history
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPerPage] = useState(10);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Receipt modal state
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    // Refund modal state
    const [showRefundModal, setShowRefundModal] = useState(false);

    // Toast state
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // Show a notice if the user landed here because their license/subscription expired
    useEffect(() => {
        const renewalMessage = sessionStorage.getItem('haazri_renewal_message');
        if (renewalMessage) {
            setToast({ show: true, message: renewalMessage, type: 'warning' });
            sessionStorage.removeItem('haazri_renewal_message');
        }
    }, []);

    useEffect(() => {
        // This page shows one organization's own subscription - core_admin
        // isn't tied to a single org here (their own org_id, if set at all,
        // doesn't represent a real billing org), so none of these calls can
        // succeed for them. Skip loading entirely rather than let
        // getSubscription() fail with a raw "Organization ID is required"
        // error - core_admin should use Payment Management instead.
        if (user && user.user_type !== 'core_admin') {
            console.log('Current user in Subscriptions:', user);
            loadSubscriptionData();
            loadPaymentPlans();
            loadPaymentGateways();
            loadPaymentHistory();
        }
    }, [user]);

    useEffect(() => {
        if (user && user.user_type !== 'core_admin' && activeTab === 'history') {
            loadPaymentHistory();
        }
    }, [historyPage, activeTab]);

    const loadSubscriptionData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await paymentsService.getSubscription();
            console.log('Loaded subscription data:', response);
            setSubscription(response);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadPaymentPlans = async () => {
        try {
            let countryCode = null;
            if (user?.org_id) {
                try {
                    const orgDetails = await organizationService.getOrganizationById(user.org_id);
                    countryCode = orgDetails?.country_code ?? null;
                } catch (_) { /* fall back to USD pricing */ }
            }

            const raw = await licenseService.getLicensePricing(countryCode);
            if (!Array.isArray(raw) || raw.length === 0) {
                setPaymentPlans([]);
                return;
            }

            const formatted = raw.map(plan => {
                const count = Number(plan.user_count);
                return {
                    id: plan.id,
                    name: `Up to ${count} user${count !== 1 ? 's' : ''}`,
                    user_count: count,
                    unit_price: Number(plan.unit_price),
                    total_price: Number(plan.total_price),
                    setup_fee: Number(plan.setup_fee),
                    currency: plan.currency,
                    country_code: plan.country_code,
                    features: {
                        user_count: `Up to ${count} user${count !== 1 ? 's' : ''}`,
                        unlimited_attendance: true,
                        advanced_reports: true,
                        mobile_app: true,
                        support: '24/7 support'
                    }
                };
            });

            setPaymentPlans(formatted);
        } catch (err) {
            console.error('Error loading payment plans:', err.message);
            setPaymentPlans([]);
        } finally {
            setPlansLoaded(true);
        }
    };

    const loadPaymentGateways = async () => {
        try {
            const response = await paymentsService.getPaymentGateways();
            setPaymentGateways(response.payment_gateways || []);
        } catch (err) {
            console.error('Error loading payment gateways:', err);
        }
    };

    const loadPaymentHistory = async () => {
        setHistoryLoading(true);
        try {
            const offset = (historyPage - 1) * historyPerPage;
            const response = await paymentsService.getPaymentHistory({
                limit: historyPerPage,
                offset: offset
            });
            setPaymentHistory(response.data || []);
            setHistoryTotal(response.total || 0);
        } catch (err) {
            console.error('Error loading payment history:', err);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleViewReceipt = (payment) => {
        setSelectedPayment(payment);
        setShowReceiptModal(true);
    };

    const handleSelectPlan = (plan) => {
        // Redirect to LicensePurchase page with the selected plan's user count
        // The LicensePurchase component has proper staff selection functionality
        navigate(`/subscriptions/purchase?staff_count=${plan.user_count}`);
    };

    const handlePaymentMethodSelect = async (gatewayCode) => {
        setShowPaymentModal(false);

        const gateway = paymentGateways.find(g => g.code === gatewayCode);

        if (gatewayCode === 'bank_transfer') {
            // Handle manual bank transfer
            await handleManualPayment(gateway);
        } else {
            // Handle automated payment gateways
            await handleAutomatedPayment(gateway);
        }
    };

    const handleManualPayment = async (gateway) => {
        try {
            setLoading(true);

            // Prepare payment data with correct parameters
            const paymentData = {
                organization_id: user.org_id,
                staff_count: selectedPlan.user_count,
                gateway_code: gateway.code,
                selected_staff_ids: [] // Empty for now - user needs to select staff separately
            };

            const response = await paymentsService.initiatePayment(paymentData);

            setManualPaymentData({
                amount: response.amount,
                currency: response.currency,
                reference: response.reference
            });
            setShowManualPaymentModal(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAutomatedPayment = async (gateway) => {
        try {
            setLoading(true);

            // Prepare payment data with correct parameters
            const paymentData = {
                organization_id: user.org_id,
                staff_count: selectedPlan.user_count,
                gateway_code: gateway.code,
                selected_staff_ids: [] // Empty for now - user needs to select staff separately
            };

            const response = await paymentsService.initiatePayment(paymentData);

            // Handle different gateway types
            switch (gateway.code) {
                case 'paystack':
                    paymentsService.initializePaystack(
                        response,
                        (paystackResponse) => handlePaymentSuccess(paystackResponse.reference),
                        () => setLoading(false)
                    );
                    break;

                case 'razorpay':
                    paymentsService.initializeRazorpay(
                        response,
                        (razorpayResponse) => handlePaymentSuccess(razorpayResponse.razorpay_payment_id),
                        () => setLoading(false)
                    );
                    break;

                case 'stripe':
                case 'flutterwave':
                    if (response.authorization_url) {
                        window.location.href = response.authorization_url;
                    }
                    break;

                case 'jazzcash':
                case 'easypaisa':
                    // These require form submission
                    submitPaymentForm(response.post_url, response.form_data);
                    break;

                default:
                    if (response.authorization_url) {
                        paymentsService.openPaymentWindow(response.authorization_url);
                    }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const submitPaymentForm = (url, formData) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;

        Object.keys(formData).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = formData[key];
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
    };

    const handlePaymentSuccess = async (reference) => {
        try {
            await paymentsService.verifyPayment({
                gateway_code: 'paystack', // Should be dynamic based on gateway
                reference: reference
            });

            alert('Payment successful! Your subscription has been activated.');
            await loadSubscriptionData();
        } catch (err) {
            setError('Payment verification failed: ' + err.message);
        }
    };

    const handleManualPaymentConfirm = async () => {
        try {
            await paymentsService.confirmManualPayment(manualPaymentData.reference);
            setShowManualPaymentModal(false);
            setManualPaymentData(null);
            alert('Payment confirmation received. Your subscription will be activated once payment is verified.');
            await loadSubscriptionData();
        } catch (err) {
            throw new Error('Failed to confirm payment: ' + err.message);
        }
    };

    const handleDiscountRequest = async () => {
        setShowDiscountModal(false);
        alert('Your discount request has been submitted. You will be notified once it is reviewed.');
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
            return;
        }

        const reason = prompt('Please provide a reason for cancellation (optional):');

        try {
            await paymentsService.cancelSubscription(reason || '');
            await loadSubscriptionData();
            alert('Subscription cancelled successfully.');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancelPendingSubscription = async () => {
        if (!subscription?.id) return;

        if (!window.confirm('Are you sure you want to cancel this pending subscription? You can always create a new one later.')) {
            return;
        }

        try {
            setLoading(true);
            await paymentsService.cancelPendingSubscription(subscription.id);
            await loadSubscriptionData();
            alert('Pending subscription cancelled successfully.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRetryPayment = async () => {
        if (!subscription) return;

        try {
            setLoading(true);
            // Navigate to purchase page with the same staff count
            navigate(`/subscriptions/purchase?staff_count=${subscription.staff_count}&retry=true`);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeSubscription = async (planId) => {
        if (!window.confirm('Are you sure you want to change your subscription plan?')) {
            return;
        }

        try {
            await paymentsService.changeSubscription(planId);
            await loadSubscriptionData();
            alert('Subscription plan changed successfully.');
        } catch (err) {
            setError(err.message);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return '#10b981';
            case 'expired': return '#f59e0b';
            case 'cancelled': return '#ef4444';
            case 'pending': return '#6366f1';
            case 'confirming': return '#3b82f6';
            default: return '#6b7280';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            // Some currencies (e.g. PKR) default to 0 fraction digits in Intl's
            // CLDR data even though this app always bills in 2 decimal places -
            // force it explicitly so the figure isn't silently rounded.
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return '∞'; // Infinity for free tier
        const end = new Date(endDate);
        const now = new Date();
        const diffTime = end - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    const getPlanName = (subscription) => {
        const staffCount = subscription.staff_count;

        // Free tier (1-4 users)
        if (staffCount < 5) {
            return 'Free Tier';
        }

        // Paid tiers - show range
        return `Up to ${staffCount} Staff`;
    };

    const getPlanDescription = (subscription) => {
        const staffCount = subscription.staff_count;

        if (staffCount < 5) {
            return 'Free forever - Perfect for small teams';
        }

        return `Professional plan for ${staffCount} staff members`;
    };

    const formatEndDate = (endDate) => {
        if (!endDate) {
            return '∞ (Never Expires)';
        }
        return formatDate(endDate);
    };

    const formatMonthlyCost = (subscription) => {
        if (subscription.staff_count < 5) {
            return 'Free Plan';
        }
        return formatCurrency(subscription.amount || subscription.monthly_cost, subscription.currency);
    };

    // Show loading state if user is not yet loaded
    if (!user) {
        return (
            <div className="subscriptions">
                <div className="container">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    // core_admin has no single org's subscription to show on this page -
    // point them at Payment Management, which covers subscriptions across
    // all organizations.
    if (user.user_type === 'core_admin') {
        return (
            <div className="subscriptions">
                <div className="container">
                    <div className="page-header">
                        <h1>Subscriptions & Billing</h1>
                    </div>
                    <div className="info-message">
                        <p>
                            As a core administrator, view and manage subscriptions across all organizations from{' '}
                            <a href="#/admin/payment-management" onClick={(e) => { e.preventDefault(); navigate('/admin/payment-management'); }}>
                                Payment Management
                            </a>.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="subscriptions">
            <div className="container">
                {/* Header */}
                <div className="page-header">
                    <div className="header-left">
                        <h1>Subscriptions & Billing</h1>
                        <p>Manage your subscription plans and payment history</p>
                    </div>
                </div>

                {error && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        {error}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-btn ${activeTab === 'subscription' ? 'active' : ''}`}
                        onClick={() => setActiveTab('subscription')}
                    >
                        <i className="fas fa-credit-card"></i>
                        Current Subscription
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
                        onClick={() => setActiveTab('plans')}
                    >
                        <i className="fas fa-th-list"></i>
                        Available Plans
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <i className="fas fa-history"></i>
                        Payment History
                    </button>
                </div>

                {loading && !showPaymentModal ? (
                    <LoadingSpinner message="Loading subscription data..." />
                ) : (
                    <>
                        {/* Current Subscription Tab */}
                        {activeTab === 'subscription' && (
                            <div className="subscription-overview">
                                {subscription ? (
                                    <SubscriptionInvoice
                                        subscription={subscription}
                                        onCancel={handleCancelPendingSubscription}
                                        onRetryPayment={handleRetryPayment}
                                        onRequestRefund={() => setShowRefundModal(true)}
                                        loading={loading}
                                    />
                                ) : (
                                    <div className="no-subscription">
                                        <i className="fas fa-credit-card"></i>
                                        <h3>No Active Subscription</h3>
                                        <p>You don't have an active subscription. Choose a plan to get started.</p>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => setActiveTab('plans')}
                                        >
                                            View Plans
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Available Plans Tab */}
                        {activeTab === 'plans' && (
                            <div className="plans-section">
                                <div className="plan-selector-container">
                                    <h2>Select a License Plan</h2>
                                    <p className="section-subtitle">Choose the number of staff members for your organisation</p>

                                    {!plansLoaded ? (
                                        <div className="plans-loading">
                                            <i className="fas fa-spinner fa-spin"></i>
                                            <p>Loading available plans…</p>
                                        </div>
                                    ) : paymentPlans.length === 0 ? (
                                        <div className="plans-loading">
                                            <i className="fas fa-exclamation-circle"></i>
                                            <p>No plans available. Please try refreshing the page.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Visible pricing cards — first 10 tiers */}
                                            <div className="pricing-cards-grid">
                                                {paymentPlans
                                                    .filter(p => p.user_count <= 10)
                                                    .map(plan => (
                                                        <div
                                                            key={plan.id}
                                                            className={`pricing-card-item ${selectedPlan?.id === plan.id ? 'selected' : ''} ${plan.user_count === 5 ? 'recommended' : ''}`}
                                                            onClick={() => setSelectedPlan(plan)}
                                                        >
                                                            {plan.user_count === 5 && (
                                                                <div className="recommended-tag">Popular</div>
                                                            )}
                                                            <div className="pci-count">{plan.user_count} Staff</div>
                                                            <div className="pci-price">
                                                                {plan.total_price > 0
                                                                    ? <><span className="pci-currency">{plan.currency}</span>{parseFloat(plan.total_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="pci-period">/mo</span></>
                                                                    : <span className="pci-free">FREE</span>
                                                                }
                                                            </div>
                                                            {plan.setup_fee > 0 && (
                                                                <div className="pci-setup">Setup: {plan.currency} {parseFloat(plan.setup_fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                            )}
                                                        </div>
                                                    ))
                                                }
                                            </div>

                                            {/* Custom / larger plan search */}
                                            <div className="custom-plan-row">
                                                <span className="custom-plan-label">
                                                    <i className="fas fa-sliders-h"></i> Need more than 10 staff? Search custom plan:
                                                </span>
                                                <div className="search-dropdown-container">
                                                    <div className="search-input-wrapper">
                                                        <i className="fas fa-search"></i>
                                                        <input
                                                            type="text"
                                                            className="plan-search-input"
                                                            placeholder="Search by user count (e.g. 25)…"
                                                            value={planSearchTerm}
                                                            onChange={(e) => setPlanSearchTerm(e.target.value)}
                                                            onFocus={() => setShowPlanDropdown(true)}
                                                        />
                                                        {planSearchTerm && (
                                                            <button className="clear-search-btn" onClick={() => { setPlanSearchTerm(''); setShowPlanDropdown(false); }}>
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {showPlanDropdown && planSearchTerm && (
                                                        <>
                                                            <div className="dropdown-overlay" onClick={() => setShowPlanDropdown(false)}></div>
                                                            <div className="plans-dropdown">
                                                                {paymentPlans
                                                                    .filter(plan =>
                                                                        plan.name.toLowerCase().includes(planSearchTerm.toLowerCase()) ||
                                                                        plan.user_count.toString().includes(planSearchTerm)
                                                                    )
                                                                    .slice(0, 30)
                                                                    .map(plan => (
                                                                        <div
                                                                            key={plan.id}
                                                                            className="plan-dropdown-item"
                                                                            onClick={() => {
                                                                                setSelectedPlan(plan);
                                                                                setShowPlanDropdown(false);
                                                                                setPlanSearchTerm(plan.name);
                                                                            }}
                                                                        >
                                                                            <div className="plan-item-header">
                                                                                <span className="plan-item-name">{plan.name}</span>
                                                                                <span className="plan-item-price">{formatCurrency(plan.total_price, plan.currency)}</span>
                                                                            </div>
                                                                            {plan.setup_fee > 0 && (
                                                                                <div className="plan-item-details">
                                                                                    <span className="setup-fee">+ Setup: {formatCurrency(plan.setup_fee, plan.currency)}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                {paymentPlans.filter(p => p.name.toLowerCase().includes(planSearchTerm.toLowerCase()) || p.user_count.toString().includes(planSearchTerm)).length === 0 && (
                                                                    <div className="no-plans-found"><i className="fas fa-search"></i><p>No plans found for "{planSearchTerm}"</p></div>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Selected plan summary + proceed */}
                                            {selectedPlan && (
                                                <div className="selected-plan-details">
                                                    <h3>Selected Plan</h3>
                                                    <div className="plan-detail-card">
                                                        <div className="plan-detail-header">
                                                            <h4>{selectedPlan.name}</h4>
                                                            <div className="plan-pricing">
                                                                <div className="price-item">
                                                                    <span className="price-label">Monthly Price:</span>
                                                                    <span className="price-value">{formatCurrency(selectedPlan.total_price, selectedPlan.currency)}</span>
                                                                </div>
                                                                <div className="price-item">
                                                                    <span className="price-label">Per User:</span>
                                                                    <span className="price-value">{formatCurrency(selectedPlan.unit_price, selectedPlan.currency)}</span>
                                                                </div>
                                                                {selectedPlan.setup_fee > 0 && (
                                                                    <div className="price-item">
                                                                        <span className="price-label">Setup Fee (once):</span>
                                                                        <span className="price-value">{formatCurrency(selectedPlan.setup_fee, selectedPlan.currency)}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="plan-features">
                                                            <h5>Included Features:</h5>
                                                            <div className="features-list">
                                                                {selectedPlan.features && Object.entries(selectedPlan.features).map(([key, value]) => {
                                                                    const displayValue = typeof value === 'boolean'
                                                                        ? value ? key.replace(/_/g, ' ') : null
                                                                        : value;
                                                                    return displayValue ? (
                                                                        <div key={key} className="feature-item">
                                                                            <i className="fas fa-check-circle"></i>
                                                                            <span>{displayValue}</span>
                                                                        </div>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        </div>
                                                        <div className="plan-actions">
                                                            <button className="btn btn-primary btn-large" onClick={() => handleSelectPlan(selectedPlan)}>
                                                                <i className="fas fa-credit-card"></i> Proceed to Payment
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Payment History Tab */}
                        {activeTab === 'history' && (
                            <div className="payment-history">
                                {historyLoading ? (
                                    <LoadingSpinner message="Loading payment history..." />
                                ) : (
                                    <>
                                        <div className="history-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Transaction ID</th>
                                                        <th>Amount</th>
                                                        <th>Plan</th>
                                                        <th>Gateway</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paymentHistory.map(payment => (
                                                        <tr key={payment.id}>
                                                            <td>{formatDate(payment.created_at)}</td>
                                                            <td className="transaction-id">
                                                                {payment.reference || 'N/A'}
                                                            </td>
                                                            <td>{formatCurrency(payment.amount, payment.currency)}</td>
                                                            <td>
                                                                {payment.staff_count ? `${payment.staff_count} Staff` : 'N/A'}
                                                            </td>
                                                            <td>
                                                                {payment.gateway_code ?
                                                                    payment.gateway_code.charAt(0).toUpperCase() +
                                                                    payment.gateway_code.slice(1).replace('_', ' ')
                                                                    : 'N/A'}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="status-badge"
                                                                    style={{ backgroundColor: getStatusColor(payment.status) }}
                                                                >
                                                                    {payment.status.toUpperCase()}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {payment.status === 'completed' && (
                                                                    <button
                                                                        className="btn btn-sm btn-secondary"
                                                                        onClick={() => handleViewReceipt(payment)}
                                                                    >
                                                                        <i className="fas fa-receipt"></i>
                                                                        Receipt
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {paymentHistory.length === 0 && !historyLoading && (
                                                <div className="empty-history">
                                                    <i className="fas fa-receipt"></i>
                                                    <h3>No Payment History</h3>
                                                    <p>You haven't made any payments yet.</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Pagination Controls */}
                                        {historyTotal > historyPerPage && (
                                            <div className="pagination">
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                                                    disabled={historyPage === 1}
                                                >
                                                    <i className="fas fa-chevron-left"></i>
                                                    Previous
                                                </button>

                                                <div className="pagination-info">
                                                    <span>
                                                        Page {historyPage} of {Math.ceil(historyTotal / historyPerPage)}
                                                    </span>
                                                    <span className="total-records">
                                                        ({historyTotal} total records)
                                                    </span>
                                                </div>

                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => setHistoryPage(prev => Math.min(Math.ceil(historyTotal / historyPerPage), prev + 1))}
                                                    disabled={historyPage >= Math.ceil(historyTotal / historyPerPage)}
                                                >
                                                    Next
                                                    <i className="fas fa-chevron-right"></i>
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Payment Method Modal */}
                {showPaymentModal && selectedPlan && (
                    <PaymentMethodModal
                        gateways={paymentGateways}
                        amount={selectedPlan.price}
                        currency={selectedPlan.currency}
                        onSelect={handlePaymentMethodSelect}
                        onClose={() => setShowPaymentModal(false)}
                    />
                )}

                {/* Manual Payment Modal */}
                {showManualPaymentModal && manualPaymentData && (
                    <ManualPaymentModal
                        paymentData={manualPaymentData}
                        onConfirm={handleManualPaymentConfirm}
                        onClose={() => {
                            setShowManualPaymentModal(false);
                            setManualPaymentData(null);
                        }}
                    />
                )}

                {/* Discount Request Modal */}
                {showDiscountModal && (
                    <DiscountRequestModal
                        onSubmit={handleDiscountRequest}
                        onClose={() => setShowDiscountModal(false)}
                    />
                )}

                {/* Payment Receipt Modal */}
                {showReceiptModal && selectedPayment && (
                    <PaymentReceipt
                        payment={selectedPayment}
                        onClose={() => {
                            setShowReceiptModal(false);
                            setSelectedPayment(null);
                        }}
                    />
                )}

                {/* Refund Request Modal */}
                {showRefundModal && subscription && (
                    <RefundRequest
                        subscription={subscription}
                        onClose={() => setShowRefundModal(false)}
                        onRefundRequested={() => {
                            setShowRefundModal(false);
                            loadSubscriptionData(); // Reload to show updated refund status
                        }}
                    />
                )}

                <Toast
                    show={toast.show}
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ show: false, message: '', type: '' })}
                />
            </div>

            <style jsx>{`
                .subscriptions {
                    min-height: 100vh;
                    background: var(--light-color);
                }

                .page-header {
                    background: var(--white);
                    padding: var(--spacing-xl);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    margin-bottom: var(--spacing-xl);
                    text-align: center;
                }

                .header-left h1 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-sm);
                    font-size: 2rem;
                }

                .header-left p {
                    color: var(--text-light);
                    margin: 0;
                }

                .tab-navigation {
                    display: flex;
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    margin-bottom: var(--spacing-xl);
                    overflow: hidden;
                }

                .tab-btn {
                    flex: 1;
                    padding: var(--spacing-lg);
                    border: none;
                    background: var(--white);
                    color: var(--text-light);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    font-size: 0.9rem;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    border-bottom: 3px solid transparent;
                }

                .tab-btn:hover {
                    color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .tab-btn.active {
                    color: var(--admin-primary);
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    border-bottom-color: var(--admin-primary);
                }

                .subscription-overview {
                    display: flex;
                    justify-content: center;
                }

                .subscription-card {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                    max-width: 800px;
                    width: 100%;
                }

                .subscription-header {
                    background: linear-gradient(135deg, var(--admin-primary), var(--admin-secondary));
                    color: white;
                    padding: var(--spacing-xl);
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                }

                .subscription-info h3 {
                    margin: 0 0 var(--spacing-sm) 0;
                    font-size: 1.5rem;
                }

                .subscription-info p {
                    margin: 0;
                    opacity: 0.9;
                }

                .status-badge {
                    color: white;
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                }

                .subscription-details {
                    padding: var(--spacing-xl);
                }

                .detail-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: var(--spacing-lg);
                    margin-bottom: var(--spacing-xl);
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .detail-item .label {
                    color: var(--text-light);
                    font-size: 0.85rem;
                    font-weight: 500;
                }

                .detail-item .value {
                    color: var(--text-dark);
                    font-size: 1rem;
                    font-weight: 600;
                }

                .subscription-features h4 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-md);
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: var(--spacing-sm);
                    margin-bottom: var(--spacing-xl);
                }

                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .feature-item i {
                    color: var(--success);
                    font-size: 0.8rem;
                }

                .subscription-actions {
                    display: flex;
                    gap: var(--spacing-md);
                    flex-wrap: wrap;
                }

                .plan-selector-container {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    padding: var(--spacing-xl);
                }

                .plan-selector-container h2 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-sm);
                    font-size: 1.8rem;
                    text-align: center;
                }

                .section-subtitle {
                    color: var(--text-light);
                    text-align: center;
                    margin-bottom: var(--spacing-xl);
                }

                .plan-selector-wrapper {
                    max-width: 800px;
                    margin: 0 auto;
                }

                .search-dropdown-container {
                    position: relative;
                    margin-bottom: var(--spacing-xl);
                }

                .search-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .search-input-wrapper i.fa-search {
                    position: absolute;
                    left: var(--spacing-md);
                    color: var(--text-light);
                    font-size: 1rem;
                }

                .plan-search-input {
                    width: 100%;
                    padding: var(--spacing-md) var(--spacing-xl) var(--spacing-md) calc(var(--spacing-xl) + var(--spacing-md));
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 1rem;
                    transition: all 0.3s ease;
                }

                .plan-search-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .clear-search-btn {
                    position: absolute;
                    right: var(--spacing-md);
                    background: none;
                    border: none;
                    color: var(--text-light);
                    cursor: pointer;
                    padding: var(--spacing-xs);
                    font-size: 1rem;
                    transition: color 0.2s ease;
                }

                .clear-search-btn:hover {
                    color: var(--error);
                }

                .dropdown-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 999;
                }

                .plans-dropdown {
                    position: absolute;
                    top: calc(100% + var(--spacing-sm));
                    left: 0;
                    right: 0;
                    background: var(--white);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    box-shadow: var(--shadow-lg);
                    max-height: 400px;
                    overflow-y: auto;
                    z-index: 1000;
                }

                .plan-dropdown-item {
                    padding: var(--spacing-md);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                    cursor: pointer;
                    transition: background 0.2s ease;
                }

                .plan-dropdown-item:hover {
                    background: rgba(var(--admin-primary-rgb), 0.05);
                }

                .plan-dropdown-item:last-child {
                    border-bottom: none;
                }

                .plan-item-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: var(--spacing-xs);
                }

                .plan-item-name {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 1rem;
                }

                .plan-item-price {
                    font-weight: 700;
                    color: var(--admin-primary);
                    font-size: 1.1rem;
                }

                .plan-item-details {
                    display: flex;
                    gap: var(--spacing-md);
                    font-size: 0.85rem;
                    color: var(--text-light);
                }

                .setup-fee {
                    color: var(--warning);
                }

                .no-plans-found {
                    padding: var(--spacing-xl);
                    text-align: center;
                    color: var(--text-light);
                }

                .no-plans-found i {
                    font-size: 2rem;
                    margin-bottom: var(--spacing-sm);
                    opacity: 0.5;
                }

                .selected-plan-details {
                    margin-top: var(--spacing-xl);
                }

                .selected-plan-details h3 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-md);
                    font-size: 1.3rem;
                }

                .plan-detail-card {
                    background: linear-gradient(135deg, rgba(var(--admin-primary-rgb), 0.05), rgba(var(--admin-secondary-rgb), 0.05));
                    border: 2px solid var(--admin-primary);
                    border-radius: var(--radius-xl);
                    padding: var(--spacing-xl);
                }

                .plan-detail-header h4 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-md);
                    font-size: 1.5rem;
                }

                .plan-pricing {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: var(--spacing-md);
                    margin-bottom: var(--spacing-xl);
                }

                .price-item {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xs);
                }

                .price-label {
                    color: var(--text-light);
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .price-value {
                    color: var(--admin-primary);
                    font-size: 1.3rem;
                    font-weight: 700;
                }

                .plan-features h5 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-md);
                    font-size: 1.1rem;
                }

                .features-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: var(--spacing-sm);
                    margin-bottom: var(--spacing-xl);
                }

                .features-list .feature-item {
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .features-list .feature-item i {
                    color: var(--success);
                    font-size: 1rem;
                }

                .plan-actions {
                    display: flex;
                    justify-content: center;
                }

                .btn-large {
                    padding: var(--spacing-md) var(--spacing-xl);
                    font-size: 1.1rem;
                    min-width: 250px;
                }

                .plans-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: var(--spacing-xl);
                }

                .plan-card {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    padding: var(--spacing-xl);
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }

                .plan-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-lg);
                }

                .plan-header {
                    text-align: center;
                    margin-bottom: var(--spacing-lg);
                }

                .plan-header h3 {
                    color: var(--text-dark);
                    margin-bottom: var(--spacing-md);
                    font-size: 1.3rem;
                }

                .plan-price {
                    margin-bottom: var(--spacing-md);
                }

                .plan-price .price {
                    font-size: 2rem;
                    font-weight: bold;
                    color: var(--admin-primary);
                }

                .plan-price .period {
                    color: var(--text-light);
                    font-size: 0.9rem;
                }

                .plan-header p {
                    color: var(--text-light);
                    margin: 0;
                }

                .plan-features {
                    flex: 1;
                    margin-bottom: var(--spacing-lg);
                }

                .payment-history {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    overflow: hidden;
                }

                .history-table {
                    overflow-x: auto;
                }

                .history-table table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .history-table th,
                .history-table td {
                    padding: var(--spacing-md);
                    text-align: left;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .history-table th {
                    background: var(--light-color);
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .history-table tr:hover {
                    background: rgba(var(--admin-primary-rgb), 0.02);
                }

                .transaction-id {
                    font-family: monospace;
                    font-size: 0.8rem;
                }

                .no-subscription,
                .empty-history {
                    text-align: center;
                    padding: var(--spacing-xl) * 2;
                    color: var(--text-light);
                }

                .no-subscription i,
                .empty-history i {
                    font-size: 3rem;
                    margin-bottom: var(--spacing-lg);
                    color: var(--admin-primary);
                    opacity: 0.5;
                }

                .no-subscription h3,
                .empty-history h3 {
                    margin-bottom: var(--spacing-sm);
                    color: var(--text-dark);
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .payment-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow: hidden;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                    overflow-y: auto;
                }

                .plan-summary {
                    background: var(--light-color);
                    padding: var(--spacing-lg);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    text-align: center;
                }

                .plan-summary h4 {
                    margin: 0 0 var(--spacing-sm) 0;
                    color: var(--text-dark);
                }

                .price-summary .amount {
                    font-size: 1.5rem;
                    font-weight: bold;
                    color: var(--admin-primary);
                }

                .price-summary .period {
                    color: var(--text-light);
                }

                .form-group {
                    margin-bottom: var(--spacing-lg);
                }

                .form-group label {
                    display: block;
                    margin-bottom: var(--spacing-sm);
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    width: 100%;
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    transition: border-color 0.2s ease;
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                }

                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    margin-top: var(--spacing-xl);
                    padding-top: var(--spacing-lg);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover {
                    background: var(--admin-secondary);
                }

                .btn-secondary {
                    background: var(--light-color);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover {
                    background: #e9ecef;
                }

                .btn-danger {
                    background: var(--error);
                    color: white;
                }

                .btn-danger:hover {
                    background: #c53030;
                }

                .btn-sm {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    font-size: 0.8rem;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-xl);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                @media (max-width: 768px) {
                    .tab-navigation {
                        flex-direction: column;
                    }

                    .tab-btn {
                        border-bottom: none;
                        border-right: 3px solid transparent;
                    }

                    .tab-btn.active {
                        border-bottom-color: transparent;
                        border-right-color: var(--admin-primary);
                    }

                    .plans-grid {
                        grid-template-columns: 1fr;
                    }

                    .detail-grid {
                        grid-template-columns: 1fr;
                    }

                    .features-grid {
                        grid-template-columns: 1fr;
                    }

                    .subscription-header {
                        flex-direction: column;
                        gap: var(--spacing-md);
                        text-align: center;
                    }

                    .modal-actions {
                        flex-direction: column;
                    }

                    .history-table {
                        font-size: 0.8rem;
                    }

                    .history-table th,
                    .history-table td {
                        padding: var(--spacing-sm);
                    }
                }
            `}</style>
        </div>
    );
};

export default Subscriptions;